/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { useState, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet, useParams, useOutletContext, useNavigate, Link, useLocation } from 'react-router';
import useSWR from 'swr';

import { authMiddleware, getFetcher, useCheckOrg, useGet, useUser } from '../net/commons';

import DiscordLogo from '@/assets/discordLogo';
import { OrganisationData } from '@/commons/commons';
import { Project, TestSuite } from '@/commons/protocols';
import Logo from '@/components/atoms/Logo';
import { ImpersonationCountdown } from '@/components/ImpersonationCountdown';
import { Toaster } from '@/components/ui/toaster';
// import { Button }  from '../components/ui/button';
// @ts-ignore
import MainNav from '@/nav/MainNav';
import NavSwitcher from '@/nav/NavSwitcher';
import { Search } from '@/nav/Search';
import { UserNav } from '@/nav/UserNav';
import { GlobalError } from '@/routes';

type UserContextType = {
    user: { id: number; isAdmin: boolean; isImpersonated: boolean };
    organisation: OrganisationData;
};

type RootProps = {
    fullScreen?: boolean;
};

const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return {
        width,
        height,
    };
};

export const useWindowDimensions = () => {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        const handleResize = () => {
            setWindowDimensions(getWindowDimensions());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowDimensions;
};

export default function Root({ fullScreen }: RootProps) {
    const { projectSlug, suiteSlug } = useParams();
    const { organisation } = useCheckOrg();
    const { user } = useUser();
    const navigate = useNavigate();
    const location = useLocation();

    const { data: projects, isLoading: isProjectsLoading } = useGet<Project[]>('/bff/projects');

    // Load this only if we have a project selected
    const { data: suites, isLoading: isSuitesLoading }: { data: TestSuite[]; isLoading: boolean } = useSWR(
        () => (projectSlug ? `/bff/project/${projectSlug}/suites` : false),
        getFetcher,
        { use: [authMiddleware] },
    );

    const getLogoColor = () => {
        if (window?.location?.href?.includes('localhost')) return 'red';
        else if (window?.location?.href?.includes('staging')) return '#1e90FF';
        return undefined;
    };

    return (
        <>
            <div className={`flex flex-col min-h-screen pb-1 ${fullScreen ? 'h-screen w-screen' : ''}`}>
                <div className="px-4">
                    <div className="flex flex-row items-center py-2 h-14">
                        <Link to="/">
                            <Logo className="h-8 w-8 mr-4" color={getLogoColor()} />
                        </Link>
                        {!isProjectsLoading && projects && (
                            <NavSwitcher
                                popOverWidth="min-w-[200px] w-fit"
                                className="mr-4"
                                currentKey={projectSlug ?? ''}
                                items={projects.map(({ slug, name }) => {
                                    return { key: slug, value: name };
                                })}
                                handleSwitch={(v) => {
                                    navigate(`/project/${v}`);
                                }}
                                handleCreate={() => {
                                    navigate(`project/create`);
                                }}
                                label="project"
                            />
                        )}
                        {suiteSlug && !isSuitesLoading && suites && (
                            <NavSwitcher
                                popOverWidth="min-w-[200px] w-fit"
                                currentKey={suiteSlug ?? ''}
                                items={suites.map(({ slug, name }) => {
                                    return { key: slug, value: name };
                                })}
                                handleSwitch={(v) => {
                                    navigate(`/suite/${projectSlug}/${v}`);
                                }}
                                handleCreate={() => {
                                    navigate(`/suite/${projectSlug}/create`);
                                }}
                                label="suite"
                            />
                        )}
                        {location && location.pathname.includes('localhost') && <div className="bg-yellow">Local</div>}

                        <MainNav className="mx-4" currentProjectSlug={projectSlug ?? ''} suiteSlug={suiteSlug} />

                        <div className="ml-auto flex items-center space-x-4">
                            {false && <Search />} {/*FIXME: enable search when search works*/}
                            <div className="flex flex-row gap-1 items-center">
                                <Link
                                    to="https://heal.dev/docs/intro"
                                    className="block pr-2 font-normal text-sm
                                        text-muted-foreground hover:bg-gray-50 rounded-sm px-1"
                                >
                                    docs
                                </Link>
                                <Link
                                    to="https://discord.gg/G3qqtdfgPG"
                                    className="flex flex-row items-center
                                            font-normal  text-sm text-muted-foreground
                                            hover:bg-gray-50 rounded-sm px-1"
                                >
                                    <div className="h-5 w-5 pt-0. 5 p-1 flex items-center">
                                        <DiscordLogo />
                                    </div>
                                    discord
                                </Link>
                            </div>
                            <UserNav hasAdminPermissions={(user?.isSuperUser || !!user?.impersonatedBy) ?? false} />
                        </div>
                    </div>
                </div>
                <div
                    className={`${fullScreen ? 'h-full min-h-0' : 'flex-1 p-8 pt-6 rounded-[10px] bg-white mx-[6px] drop-shadow-[0_0_10px_rgba(0,0,0,0.05)]'}`}
                >
                    <ErrorBoundary FallbackComponent={GlobalError}>
                        <Outlet
                            context={{
                                user: {
                                    id: user?.id,
                                    isAdmin: user?.isSuperUser,
                                    isImpersonated: user?.impersonatedBy,
                                },
                                organisation: {
                                    id: organisation?.id,
                                    name: organisation?.name,
                                    slackWebhookUrl: organisation?.slackWebhookUrl,
                                },
                            }}
                        />
                    </ErrorBoundary>
                </div>
            </div>
            <Toaster />
            {user?.impersonatedBy && <ImpersonationCountdown />}
        </>
    );
}

export const useUserContext = () => {
    return useOutletContext<UserContextType>();
};
